import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from './icons.js';

export type ViewerPhoto = { id: string; alt: string; caption?: string | null };

/**
 * Visionneuse de photo intégrée (03/10/2026) : superposition plein écran sombre, au-dessus de la page,
 * sans quitter l'application (l'ouverture dans un nouvel onglet laissait l'application installée sur
 * iPhone sans moyen de revenir). Croix de fermeture en haut à droite (48 px), Échap, appui sur le fond
 * et bouton « retour » du téléphone ferment la visionneuse. Le retour repose sur un état d'historique
 * poussé SANS changer l'adresse : le fragment (jeton du portail) n'est jamais touché.
 *
 * L'image elle-même est rendue par l'appelant (`render`) : URL d'API contrôlée côté garage, image
 * chargée avec le jeton côté portail. Rien n'est mis en cache ici.
 */
export function PhotoViewer({
  photos,
  index,
  onIndexChange,
  onClose,
  render,
  meta,
  actions,
}: {
  photos: ViewerPhoto[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  render: (photo: ViewerPhoto) => ReactNode;
  /** Ligne secondaire sous la légende (date de prise de vue…). */
  meta?: (photo: ViewerPhoto) => ReactNode;
  /** 08/10/2026 : actions sur la photo affichée (ex. « Proposer des travaux »), en bas, à portée du pouce. */
  actions?: (photo: ViewerPhoto) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const marker = useId();
  const pushed = useRef(false);
  const pendingBack = useRef<number | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const touchX = useRef<number | null>(null);
  const photo = photos[index];
  const count = photos.length;

  // Historique : un état propre à la visionneuse, même adresse (fragment compris).
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Double montage du mode strict : l'état poussé est toujours le nôtre, on le garde.
    if (pendingBack.current !== null) {
      clearTimeout(pendingBack.current);
      pendingBack.current = null;
    } else {
      window.history.pushState({ ...(window.history.state as object | null), photoViewer: marker }, '');
    }
    pushed.current = true;
    const onPop = () => {
      pushed.current = false;
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = overflow;
      if (pushed.current) {
        // Fermeture par la croix, Échap ou le fond : on retire l'état poussé (sans toucher l'adresse).
        pendingBack.current = window.setTimeout(() => {
          pendingBack.current = null;
          pushed.current = false;
          if ((window.history.state as { photoViewer?: string } | null)?.photoViewer === marker) window.history.back();
        }, 0);
      }
      opener?.focus();
    };
  }, [marker]);

  const close = useCallback(() => onCloseRef.current(), []);
  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < count) onIndexChange(next);
    },
    [index, count, onIndexChange],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowRight') go(1);
    else if (e.key === 'ArrowLeft') go(-1);
    else if (e.key === 'Tab' && ref.current) {
      // Focus piégé dans la visionneuse.
      const items = [...ref.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!ref.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  if (!photo) return null;
  const nav = 'grid size-12 place-items-center rounded-full bg-black/50 text-white hover:bg-white/15 disabled:opacity-0';

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        const end = e.changedTouches[0]?.clientX;
        touchX.current = null;
        if (start === null || end === undefined) return;
        if (end - start > 50) go(-1);
        else if (start - end > 50) go(1);
      }}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] text-white"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 p-2">
        <span id={titleId} className="px-2 text-sm opacity-80 tabular-nums">
          {count > 1 ? `Photo ${index + 1} sur ${count}` : 'Photo'}
        </span>
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          aria-label="Fermer"
          className="grid size-12 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 focus-visible:outline-2 focus-visible:outline-white"
        >
          <CloseIcon size={26} />
        </button>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-2"
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <div key={photo.id} className="flex max-h-full max-w-full items-center justify-center">
          {render(photo)}
        </div>
        {count > 1 ? (
          <>
            <button type="button" aria-label="Photo précédente" disabled={index === 0} onClick={() => go(-1)} className={`${nav} absolute top-1/2 left-2 -translate-y-1/2`}>
              <ChevronLeftIcon size={26} />
            </button>
            <button type="button" aria-label="Photo suivante" disabled={index === count - 1} onClick={() => go(1)} className={`${nav} absolute top-1/2 right-2 -translate-y-1/2`}>
              <ChevronRightIcon size={26} />
            </button>
          </>
        ) : null}
      </div>
      {photo.caption || meta ? (
        <div className="shrink-0 space-y-1 p-4">
          {photo.caption ? <p className="text-base">{photo.caption}</p> : null}
          {meta ? <p className="text-sm opacity-70">{meta(photo)}</p> : null}
        </div>
      ) : null}
      {actions ? <div className="flex shrink-0 justify-center gap-2 px-4 pb-4">{actions(photo)}</div> : null}
    </div>,
    document.body,
  );
}

/** État d'ouverture d'une visionneuse : index de la photo ouverte, ou null. */
export function usePhotoViewer() {
  const [open, setOpen] = useState<number | null>(null);
  return { open, setOpen, close: useCallback(() => setOpen(null), []) };
}
